import { ChatPanelView } from "@bezoned/shared/components/chat/chat-panel-view.jsx";
import { FloatingWindowCard } from "@bezoned/shared/components/floating-window/floating-window-card.jsx";
import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import { useCallback, useEffect } from "react";

import { useAuth } from "../../../features/auth/use-auth.js";
import { useChatState } from "../../../features/chat/chat-state.provider.js";
import { useChatUnreadStore } from "../../../features/chat/chat-unread.store.js";
import { useSlideShowHideComponent } from "../../../hooks/use-slide-show-hide-component.js";
import { useLayoutStore } from "../../../store/layout.store.js";

interface ChatFrontendContainerProps {
  isVisible: boolean;
}

export const ChatFrontendContainer = ({
  isVisible,
}: ChatFrontendContainerProps) => {
  const { user } = useAuth();
  const {
    officeCredentials,
    officeTopic,
    isOfficeLoading,
    isOfficeInitError,
    retryOfficeChat,
    officeRefreshToken,
    tableCredentials,
    tableTopic,
    isLoadingTableChat,
    isTableInitError,
    retryTableChat,
    tableRefreshToken,
  } = useChatState();
  const { setOfficeUnread, setTableUnread } = useChatUnreadStore();

  const handleUnreadChange = useCallback(
    ({
      officeUnread,
      tableUnread,
    }: {
      officeUnread: boolean;
      tableUnread: boolean;
    }) => {
      setOfficeUnread(officeUnread);
      setTableUnread(tableUnread);
    },
    [setOfficeUnread, setTableUnread]
  );

  return (
    <ChatPanelView
      isVisible={isVisible}
      notificationSoundSrc="/audio/notification.wav"
      office={{
        credentials: officeCredentials,
        isLoading: isOfficeLoading,
        onRetry: isOfficeInitError ? retryOfficeChat : undefined,
        refreshToken: officeRefreshToken ?? undefined,
        topic: officeTopic,
      }}
      onUnreadChange={handleUnreadChange}
      reserveHeaderEndSpace
      table={{
        credentials: tableCredentials,
        isLoading: isLoadingTableChat,
        onRetry: isTableInitError ? retryTableChat : undefined,
        refreshToken: tableRefreshToken ?? undefined,
        topic: tableTopic,
      }}
      userName={user.name}
    />
  );
};

export const ChatWindow = () => {
  const chatVisible = useLayoutStore((state) => state.chatVisible);
  const setChatVisible = useLayoutStore((state) => state.setChatVisible);

  useEffect(() => {
    const handler = (e: CustomEvent<{ id: string; value: boolean }>) => {
      if (e.detail.id === "chat") {
        setChatVisible(e.detail.value);
      }
    };
    document.addEventListener("toggleComponent", handler as EventListener);
    return () =>
      document.removeEventListener("toggleComponent", handler as EventListener);
  }, [setChatVisible]);

  const { style, shouldRender } = useSlideShowHideComponent(chatVisible, {
    direction: "right",
    duration: 300,
    id: "chat",
    label: "Chat",
    position: {
      right: 0,
      top: "50%",
    },
  });

  const closeChat = useCallback(() => {
    setChatVisible(false, { persistPreference: true });
  }, [setChatVisible]);
  const handleClick113 = useStableCallback((event) => event.stopPropagation());

  if (!shouldRender) {
    return null;
  }

  return (
    // Lint exception: Prevents clicks from propagating to the 3D scene
    // Lint exception: No keyboard interaction needed - only stops event propagation
    // Lint exception: Container is not interactive, only catches clicks
    <div
      className="glass absolute right-[15px] bottom-4 z-50 rounded-xl p-2"
      data-blocks-hover="true"
      onClick={handleClick113}
      style={style}
    >
      <FloatingWindowCard className="h-[450px] w-[297px]" onClose={closeChat}>
        <ChatFrontendContainer isVisible={chatVisible} />
      </FloatingWindowCard>
    </div>
  );
};
